import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { NoIndexMeta } from '@/components/seo/no-index-meta'
import { PageMeta } from '@/components/seo/page-meta'
import {
  AgeSection,
  Checklist,
  EmployeeBaseline,
  FamilySection,
  HealthSection,
  RetrainingSection,
  UnemploymentSection,
  ViewSwitch,
} from '@/components/severance-atlas/coverage-sections'
import {
  Badge,
  HEAT,
  Pill,
  RuleGrid,
  SectionHead,
  StateChip,
  cardClass,
  eyebrowClass,
  pillBase,
  pip,
  scrollToId,
} from '@/components/severance-atlas/primitives'
import { UNEMPLOYMENT_LEVELS } from '@/content/severance-atlas/coverage'
import {
  type AtlasState,
  BASE,
  D,
  DISC_BY_ID,
  EXCEPTIONS,
  LENSES,
  type LensKey,
  SOURCE_ARTICLES,
  STATES,
  STATE_BY_CODE,
  STATS,
  TILE_POS,
  countWhere,
} from '@/content/severance-atlas/model'
import {
  HERO_COPY,
  SECTION_COPY,
  SECTION_ORDER,
  type SectionId,
  type View,
} from '@/content/severance-atlas/perspective'
import { VERIFICATION_SOURCES } from '@/content/severance-atlas/sources'
import { PELGO_SEVERANCE_PLAYBOOK_SEO } from '@/lib/pelgo-seo'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

const SEV_STYLE = {
  high: 'bg-red-50 text-red-700',
  med: 'bg-amber-50 text-amber-800',
  low: 'bg-gray-100 text-gray-600',
} as const
const SEV_NAME = { high: 'High', med: 'Medium', low: 'Low' } as const

const VIEW_KEY = 'pelgo-severance-atlas-view'

const PTO_LABEL = ['Policy governs', '', 'Unless written policy', 'Required']

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-gray-100 px-5 py-3 last:border-b-0 sm:grid-cols-[130px_1fr] sm:gap-4">
      <dt className="pt-0.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="m-0 space-y-1 text-sm text-gray-800">{children}</dd>
    </div>
  )
}

function StateDetail({
  s,
  view,
  onJump,
}: { s: AtlasState; view: View; onJump: (id: string) => void }) {
  const V = (er: string, ee: string) => (view === 'ee' ? ee : er)
  return (
    <div className={`${cardClass} overflow-hidden`} aria-live="polite">
      <div className="space-y-3 border-b border-gray-100 px-5 py-4">
        <h3 className="flex items-baseline gap-3 text-2xl font-display font-medium tracking-[-0.4px] text-gray-900">
          {s.n}
          <small className="text-sm font-medium text-gray-400">{s.c}</small>
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {s.sev[0] >= 2 ? <Badge tone="hot">Severance required</Badge> : null}
          {s.sev[0] === 1 ? <Badge tone="warm">Health continuation mandate</Badge> : null}
          {s.warn[0] === 2 ? <Badge tone="hot">Mini-WARN</Badge> : null}
          {s.warn[0] === 1 ? <Badge tone="warm">Voluntary/limited WARN</Badge> : null}
          {s.fire[0] === 3 ? <Badge tone="hot">Fast final pay</Badge> : null}
          {s.pto[0] === 3 ? <Badge tone="hot">PTO payout required</Badge> : null}
          {s.sep ? <Badge tone="warm">Separation notice</Badge> : null}
          {s.disc.length ? (
            <Badge tone="flag">
              {s.disc.length} source conflict{s.disc.length > 1 ? 's' : ''}
            </Badge>
          ) : null}
          {!s.sev[0] && !s.warn[0] && s.fire[0] !== 3 && !s.pto[0] && !s.sep && !s.disc.length ? (
            <Badge tone="plain">Federal baseline only</Badge>
          ) : null}
        </div>
      </div>
      <dl className="m-0">
        <DetailRow label={V('Severance', 'Severance owed')}>
          <span>{s.sev[1]}</span>
          {view === 'ee' && s.sev[0] === 0 ? (
            <span className="block text-gray-500">
              Anything promised in your offer letter, contract, handbook or severance plan is still
              enforceable.
            </span>
          ) : null}
        </DetailRow>
        <DetailRow label={V('Layoff notice', 'Advance notice')}>
          <span>{s.warn[1]}</span>
          {s.warn[0] === 0 ? (
            <span className="block text-gray-500">Federal: 100+ employees, 60 days.</span>
          ) : null}
        </DetailRow>
        <DetailRow label={V('Final pay', 'Last paycheck')}>
          <span className="block">
            <b className="font-semibold">
              {V('Fired / laid off:', 'If you’re laid off or fired:')}
            </b>{' '}
            {s.fire[1]}
          </span>
          <span className="block">
            <b className="font-semibold">{V('Resigned:', 'If you resign:')}</b> {s.quit}
          </span>
        </DetailRow>
        <DetailRow label={V('Accrued PTO', 'Unused vacation')}>{s.pto[1]}</DetailRow>
        <DetailRow label={V('Severance & UI', 'Severance & unemployment')}>
          {UNEMPLOYMENT_LEVELS[s.ui[0]]}: {s.ui[1]}.
        </DetailRow>
        <DetailRow label={V('Health continuation', 'Keeping health coverage')}>
          <span className="block">
            {s.mc[1] === '-'
              ? 'No general state continuation law. Federal COBRA only (20+ employees).'
              : `${s.mc[2]}: up to ${s.mc[3]}.`}
          </span>
          {s.mc[4] ? <span className="block text-gray-500">{s.mc[4]}</span> : null}
          {s.mc[5] === 'x' ? (
            <span className="block text-amber-800">Sources conflict on this state.</span>
          ) : null}
        </DetailRow>
        <DetailRow label={V('Separation notice', 'Forms you should get')}>
          {s.sep ?? <span className="text-gray-500">No state-mandated separation form.</span>}
        </DetailRow>
        {s.other.length ? (
          <DetailRow label="Also note">
            <ul className="m-0 list-disc space-y-1 pl-4">
              {s.other.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          </DetailRow>
        ) : null}
        {s.disc.length ? (
          <DetailRow label="Source conflicts">
            {s.disc.map((id) => {
              const d = DISC_BY_ID[id]
              if (!d) return null
              return (
                <div key={id} className="space-y-1 rounded-xl bg-amber-50 p-3 text-amber-950">
                  <b className="block font-semibold">
                    {d.id} &middot; {d.t}
                  </b>
                  <span className="block">{d.v}</span>
                  <button
                    type="button"
                    onClick={() => onJump(id)}
                    className="font-semibold underline underline-offset-2"
                  >
                    See what each source said
                  </button>
                </div>
              )
            })}
          </DetailRow>
        ) : null}
      </dl>
    </div>
  )
}

type Col = {
  label: string
  sort: (s: AtlasState) => string | number
  cell: (s: AtlasState) => ReactNode
  bold?: boolean
}

const NAME_COL: Col = { label: 'State', sort: (s) => s.n, cell: (s) => s.n, bold: true }

const COLS: Col[] = [
  NAME_COL,
  {
    label: 'Severance',
    sort: (s) => s.sev[0],
    cell: (s) => (
      <>
        {pip(s.sev[0])}
        {s.sev[0] ? (s.sev[1].split(':')[0] ?? '').split('(')[0] : 'Contractual only'}
      </>
    ),
  },
  {
    label: 'Mini-WARN',
    sort: (s) => s.warn[0],
    cell: (s) => (
      <>
        {pip(s.warn[0])}
        {s.warn[0] ? s.warn[1].split(';').slice(0, 2).join(';') : 'Federal only'}
      </>
    ),
  },
  {
    label: 'Final pay, fired',
    sort: (s) => s.fire[0],
    cell: (s) => (
      <>
        {pip(s.fire[0])}
        {s.fire[1]}
      </>
    ),
  },
  { label: 'Final pay, resigned', sort: (s) => s.quit, cell: (s) => s.quit },
  {
    label: 'PTO payout',
    sort: (s) => s.pto[0],
    cell: (s) => (
      <>
        {pip(s.pto[0])}
        {PTO_LABEL[s.pto[0]]}
      </>
    ),
  },
  { label: 'Sep. notice', sort: (s) => (s.sep ? 1 : 0), cell: (s) => (s.sep ? 'Yes' : '-') },
  {
    label: 'Conflicts',
    sort: (s) => s.disc.length,
    cell: (s) => (s.disc.length ? s.disc.join(', ') : '-'),
  },
]

const hasException = (s: AtlasState) =>
  Boolean(s.sev[0] || s.warn[0] || s.fire[0] === 3 || s.pto[0] || s.sep || s.other.length)

export function SeveranceAdministrationPlaybookPage() {
  const [view, setView] = useState<View>('er')
  const [lens, setLens] = useState<LensKey>('sev')
  const [selected, setSelected] = useState('NJ')
  const [query, setQuery] = useState('')
  const [onlyEx, setOnlyEx] = useState(false)
  const [onlyDisc, setOnlyDisc] = useState(false)
  const [sortI, setSortI] = useState(0)
  const [sortDir, setSortDir] = useState<1 | -1>(1)
  const [sevFilter, setSevFilter] = useState<'all' | 'high' | 'med' | 'low'>('all')

  // Read the saved or linked perspective after mount, so prerendered HTML matches the first render.
  useEffect(() => {
    const hash = window.location.hash
    if (hash === '#employee') setView('ee')
    else if (hash === '#employer') setView('er')
    else {
      try {
        const saved = window.localStorage.getItem(VIEW_KEY)
        if (saved === 'ee' || saved === 'er') setView(saved)
      } catch {
        // Storage unavailable; keep the employer default.
      }
    }
  }, [])

  const changeView = (v: View) => {
    setView(v)
    try {
      window.localStorage.setItem(VIEW_KEY, v)
    } catch {
      // Storage unavailable; the choice lasts for this visit only.
    }
    window.setTimeout(() => scrollToId('checklist'), 0)
  }

  const L = LENSES[lens]
  const levelKeys = Object.keys(L.levels)
    .map(Number)
    .sort((a, b) => b - a)

  const openState = (code: string) => {
    setSelected(code)
    scrollToId('explorer')
  }
  const jumpToDisc = (id: string) => {
    setSevFilter('all')
    // Wait a tick so the card exists if the filter just changed.
    window.setTimeout(() => scrollToId(id), 0)
  }

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    const key = (COLS[sortI] ?? NAME_COL).sort
    return STATES.filter(
      (s) =>
        (!onlyEx || hasException(s)) &&
        (!onlyDisc || s.disc.length) &&
        (!q || JSON.stringify(s).toLowerCase().includes(q)),
    ).sort((a, b) => {
      const x = key(a)
      const y = key(b)
      return (x > y ? 1 : x < y ? -1 : 0) * sortDir || a.n.localeCompare(b.n)
    })
  }, [query, onlyEx, onlyDisc, sortI, sortDir])

  const onSort = (i: number) => {
    if (i === sortI) setSortDir((d) => (d === 1 ? -1 : 1))
    else {
      setSortI(i)
      setSortDir(i === 0 ? 1 : -1)
    }
  }

  const discRows = D.filter((d) => sevFilter === 'all' || d.sev === sevFilter)
  const discCount = (k: string) => D.filter((d) => k === 'all' || d.sev === k).length

  const sectionBody: Record<SectionId, ReactNode> = {
    checklist: <Checklist view={view} />,
    baseline: <>{view === 'ee' ? <EmployeeBaseline /> : <RuleGrid rules={BASE} />}</>,
    explorer: (
      <>
        <fieldset className="m-0 mb-4 flex min-w-0 flex-wrap gap-2 border-0 p-0">
          <legend className="sr-only">Map lens</legend>
          {(Object.keys(LENSES) as LensKey[]).map((k) => (
            <Pill key={k} active={lens === k} onClick={() => setLens(k)}>
              {LENSES[k].label}
            </Pill>
          ))}
        </fieldset>
        <div className="grid items-start gap-6 lg:grid-cols-[1.15fr_1fr]">
          <div className={`${cardClass} grid gap-4 p-4 md:p-6`}>
            <fieldset className="m-0 grid min-w-0 grid-cols-12 gap-1 border-0 p-0">
              <legend className="sr-only">US states tile map</legend>
              {STATES.map((s) => {
                const [col, row] = TILE_POS[s.c] ?? [0, 0]
                const v = L.get(s)
                return (
                  <button
                    key={s.c}
                    type="button"
                    aria-label={s.n}
                    aria-pressed={selected === s.c}
                    title={`${s.n}: ${L.levels[v]}`}
                    onClick={() => setSelected(s.c)}
                    style={{ gridColumn: col + 1, gridRow: row + 1 }}
                    className={`relative grid aspect-square place-items-center rounded-md text-[10px] font-semibold transition-transform hover:z-10 hover:scale-110 md:text-xs ${HEAT[v]} ${
                      selected === s.c ? 'outline outline-2 outline-offset-2 outline-gray-900' : ''
                    }`}
                  >
                    {s.c}
                    {s.disc.length ? (
                      <span
                        aria-hidden
                        className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-amber-400 ring-1 ring-amber-900/50"
                      />
                    ) : null}
                  </button>
                )
              })}
            </fieldset>
            <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-2 p-0 text-xs text-gray-700">
              {levelKeys.map((v) => (
                <li key={v} className="inline-flex items-center gap-1.5">
                  <i className={`inline-block h-3 w-3 rounded-sm ${HEAT[v]}`} />
                  {L.levels[v]}
                  <span className="text-gray-400">{countWhere((s) => L.get(s) === v)}</span>
                </li>
              ))}
            </ul>
            <p className="m-0 text-sm text-gray-500">{L.note}</p>
          </div>
          {STATE_BY_CODE[selected] ? (
            <StateDetail s={STATE_BY_CODE[selected]} view={view} onJump={jumpToDisc} />
          ) : null}
        </div>
      </>
    ),
    exceptions: (
      <>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {EXCEPTIONS.map((x) => (
            <article key={x.h} className={`${cardClass} grid content-start gap-3 p-6`}>
              <h3 className="flex items-baseline justify-between gap-3 text-base font-display font-medium leading-snug tracking-[-0.2px] text-gray-900">
                {x.h}
                {x.count ? (
                  <span className="shrink-0 text-xs font-medium text-gray-400">
                    {x.count} states
                  </span>
                ) : null}
              </h3>
              <p className="m-0 text-sm text-gray-500">{x.p}</p>
              {x.list ? (
                <ul className="m-0 grid list-none gap-2 p-0">
                  {x.list.map(([code, text]) => (
                    <li
                      key={code + text}
                      className="grid grid-cols-[38px_1fr] items-baseline gap-2 text-sm text-gray-800"
                    >
                      <StateChip code={code} onOpen={openState} />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {x.chips?.map((g) => (
                <div key={g.label ?? g.codes.join()} className="space-y-1.5">
                  {g.label ? <p className="m-0 text-sm text-gray-500">{g.label}</p> : null}
                  <div className="flex flex-wrap gap-1.5">
                    {g.codes.map((c) => (
                      <StateChip key={c} code={c} onOpen={openState} />
                    ))}
                  </div>
                </div>
              ))}
            </article>
          ))}
        </div>
      </>
    ),
    health: <HealthSection />,
    unemployment: <UnemploymentSection />,
    age: <AgeSection />,
    family: <FamilySection />,
    retraining: <RetrainingSection onOpen={openState} />,
    table: (
      <>
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={'Filter by state or text, e.g. "72 hours"'}
            aria-label="Filter states"
            className="min-w-0 max-w-sm flex-1 basis-56 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0050e6]"
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={onlyEx} onChange={(e) => setOnlyEx(e.target.checked)} />
            Only states with any exception
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={onlyDisc}
              onChange={(e) => setOnlyDisc(e.target.checked)}
            />
            Only states with discrepancies
          </label>
        </div>
        <div className={`${cardClass} overflow-x-auto`}>
          <table className="w-full min-w-[980px] border-collapse text-sm">
            <thead>
              <tr>
                {COLS.map((c, i) => (
                  <th
                    key={c.label}
                    scope="col"
                    aria-sort={i === sortI ? (sortDir === 1 ? 'ascending' : 'descending') : 'none'}
                    className="whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    <button
                      type="button"
                      onClick={() => onSort(i)}
                      className="uppercase tracking-wide"
                    >
                      {c.label}
                      {i === sortI ? (sortDir === 1 ? ' ↑' : ' ↓') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((s) => (
                  // Mouse convenience only; keyboard users open a state via the name button.
                  // biome-ignore lint/a11y/useKeyWithClickEvents: name cell holds a focusable button
                  <tr
                    key={s.c}
                    onClick={() => openState(s.c)}
                    className="cursor-pointer transition-colors hover:bg-gray-50"
                  >
                    {COLS.map((c) => (
                      <td
                        key={c.label}
                        className={`border-b border-gray-100 px-3 py-2.5 align-top text-gray-700 ${
                          c.bold ? 'whitespace-nowrap font-semibold text-gray-900' : ''
                        }`}
                      >
                        {c.bold ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              openState(s.c)
                            }}
                            className="font-semibold hover:text-[#0050e6]"
                          >
                            {c.cell(s)}
                          </button>
                        ) : (
                          c.cell(s)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={COLS.length} className="px-3 py-6 text-gray-500">
                    No states match. Clear the filter to see all 50.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    ),
    discrepancies: (
      <>
        <div className="mb-4 flex flex-wrap gap-2">
          {(['all', 'high', 'med', 'low'] as const).map((k) => (
            <Pill key={k} active={sevFilter === k} onClick={() => setSevFilter(k)}>
              {k === 'all' ? 'All' : SEV_NAME[k]} &middot; {discCount(k)}
            </Pill>
          ))}
        </div>
        <div className="grid gap-4">
          {discRows.map((d) => (
            <article key={d.id} id={d.id} className={`${cardClass} scroll-mt-28 overflow-hidden`}>
              <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-100 px-5 py-3">
                <span className="text-xs font-medium text-gray-400">{d.id}</span>
                <h3 className="min-w-[240px] flex-1 text-base font-display font-medium tracking-[-0.2px] text-gray-900">
                  {d.t}
                </h3>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${SEV_STYLE[d.sev]}`}
                >
                  {SEV_NAME[d.sev]}
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {d.states.map((c) => (
                    <StateChip key={c} code={c} onOpen={openState} />
                  ))}
                </span>
              </header>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3">
                {d.c.map(([who, text]) => (
                  <div
                    key={who}
                    className={`grid content-start gap-1 border-b border-r border-gray-100 px-5 py-3 text-sm ${
                      text ? 'text-gray-800' : 'italic text-gray-400'
                    }`}
                  >
                    <span className="text-xs font-semibold uppercase not-italic tracking-wide text-gray-400">
                      {who}
                    </span>
                    {text ?? 'Not covered'}
                  </div>
                ))}
              </div>
              <div className="grid gap-1 bg-gray-50 px-5 py-3 text-sm text-gray-800">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#0050e6]">
                  Verified
                </span>
                {d.v}
              </div>
            </article>
          ))}
        </div>
      </>
    ),
    sources: (
      <>
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h3 className="mb-3 text-base font-display font-medium text-gray-900">
              Provided articles
            </h3>
            <ul className="m-0 grid gap-2 pl-5 text-sm text-gray-700">
              {SOURCE_ARTICLES.map(([label, href, note]) => (
                <li key={href}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0050e6] hover:underline"
                  >
                    {label}
                  </a>{' '}
                  <span className="text-xs text-gray-400">{note}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-3 text-base font-display font-medium text-gray-900">
              Verification sources
            </h3>
            <ul className="m-0 grid gap-2 pl-5 text-sm text-gray-700">
              {VERIFICATION_SOURCES.map(([label, href]) => (
                <li key={href}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0050e6] hover:underline"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-8 max-w-3xl rounded-2xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
          <b className="font-semibold text-gray-700">How the data was verified.</b> Every item in
          the discrepancy section was checked against the statute or a recent law-firm or agency
          source, and the explorer, exceptions and table use only the verified value. Each final-pay
          rule either matches in two independent 50-state charts (Workforce.com and Paycor) or was
          checked against the statute where they disagree. Severance, mini-WARN, release, PTO-payout
          and service-letter rules were each checked against the statute or at least one law-firm or
          agency source. The health-continuation and severance-vs-unemployment tables were compared
          across two 50-state sources and checked against statutes or state agencies where they
          disagreed; each row shows its confidence. This is a research summary, not legal advice.
          Statutes change often, so confirm anything you plan to act on with the state labor
          department or employment counsel.
        </p>
      </>
    ),
  }
  const order = SECTION_ORDER[view]

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <PageMeta config={PELGO_SEVERANCE_PLAYBOOK_SEO} />
      <NoIndexMeta />
      <MarketingNav variant="standalone" />

      <main className="flex-1 pt-32">
        {/* Hero */}
        <div
          className="py-12 md:py-20"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
          }}
        >
          <div className="container mx-auto px-4 md:px-16">
            <div className="mx-auto max-w-[1280px]">
              <header className="max-w-3xl">
                <p className={eyebrowClass}>Severance administration playbook</p>
                <div className="mb-6 mt-4 h-3 w-12 rounded-full bg-pelgo-blue-light" aria-hidden />
                <h1 className="text-4xl font-display text-gray-900 leading-[1.2] tracking-[-0.96px] md:text-[48px]">
                  Severance &amp; Termination Law Atlas
                </h1>
                <p className="mt-6 text-base font-medium leading-relaxed tracking-[-0.32px] text-gray-500">
                  {HERO_COPY[view]}
                </p>
                <p className="mt-4 text-sm text-gray-400">
                  As of 30 Sep 2026 &middot; 4 source articles and 75 verification sources &middot;
                  Not legal advice
                </p>
                <ViewSwitch view={view} onChange={changeView} />
              </header>

              <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 md:grid-cols-3 lg:grid-cols-6">
                {STATS.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => {
                      if (s.lens) setLens(s.lens)
                      scrollToId(s.target)
                    }}
                    className="grid content-start gap-1 bg-white p-4 text-left transition-colors hover:bg-gray-50"
                  >
                    <b className="text-3xl font-display font-medium tabular-nums tracking-[-0.6px] text-pelgo-blue-dark">
                      {s.n}
                    </b>
                    <span className="text-xs leading-snug text-gray-500">{s.label}</span>
                  </button>
                ))}
              </div>

              <nav aria-label="Sections" className="mt-8 flex flex-wrap gap-2">
                {order.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault()
                      scrollToId(s.id)
                    }}
                    className={`${pillBase} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="container mx-auto space-y-20 px-4 pb-20 pt-12 md:px-16">
          <div className="mx-auto max-w-[1280px] space-y-20">
            {order.map(({ id }, i) => (
              <section key={id} id={id} className="scroll-mt-28">
                <SectionHead eyebrow={`Part ${i + 1}`} title={SECTION_COPY[id][view].title}>
                  {SECTION_COPY[id][view].body}
                </SectionHead>
                {sectionBody[id]}
              </section>
            ))}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
